import * as favoriteNodeParser from '../../utils/favoriteNodeParser';
import * as favoritesBitFlagsActions from './favoritesBitFlagsActions';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import folderStore from 'mini-mail-folder-store/lib/store/Store';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import loadFavoriteNodesToStore from './loadFavoriteNodesToStore';
import type RecursiveReadOnly from 'owa-service/lib/RecursiveReadOnly';
import tryAddSingleFavoriteFolderToStore from './tryAddSingleFavoriteFolderToStore';
import type UserOptionsType from 'owa-service/lib/contract/UserOptionsType';
import { action } from 'satcheljs/lib/legacy';
import { logUsage } from 'owa-analytics';
import { updateUserConfiguration } from 'owa-session-store/lib/actions/updateUserConfiguration';
import { updateUserConfigurationService } from 'owa-service/lib/utils/updateUserConfigurationService';

async function updateFavoritesAndMarkMigrationComplete(
    userOptions: RecursiveReadOnly<UserOptionsType>
) {
    const newFavoritesNodesRaw = favoriteNodeParser.serialize(
        folderStore.orderedFavoritesFolderIds,
        userOptions.FavoriteNodes
    );

    // Update the local user configuration store
    updateUserConfiguration(config => {
        config.UserOptions.FavoriteNodes = newFavoritesNodesRaw;
    });
    favoritesBitFlagsActions.setBit(
        true /* value*/,
        favoritesBitFlagsActions.FirstRunFavoritesMigrationFlag
    );

    try {
        // Send out request to update both UserOptions.FavoriteNodes and FavoritesBitFlags
        const response = await updateUserConfigurationService(
            [
                {
                    key: 'FavouriteNodes',
                    valuetype: 'StringArray',
                    value: newFavoritesNodesRaw,
                    /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
                     * See aka.ms/mini-lint-errors
                     *	> Unexpected any. Specify a different type. */
                } as any,
                {
                    key: 'FavouritesBitFlags',
                    valuetype: 'Integer32',
                    value: [`${userOptions.FavoritesBitFlags}`],
                    /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
                     * See aka.ms/mini-lint-errors
                     *	> Unexpected any. Specify a different type. */
                } as any,
            ],
            'OWA.UserOptions'
        );

        const responseMessage =
            response &&
            response.Body &&
            response.Body.ResponseMessages &&
            response.Body.ResponseMessages.Items
                ? response.Body.ResponseMessages.Items[0]
                : null;

        const responseClass =
            responseMessage && responseMessage.ResponseClass
                ? responseMessage.ResponseClass
                : 'Unrecognized Response Class';
        logUsage('FirstRunMigration_Status', [responseClass, userOptions.FavoritesBitFlags]);
    } catch (e) {
        logUsage('FirstRunMigration_Status', [e, userOptions.FavoritesBitFlags]);
    }
}

export default action('migrateFavorites')(() => {
    // 1. Automatically populate default folders it they don't exist in favorites
    const defaultFavoriteFolderIds = [
        folderNameToId('inbox'),
        folderNameToId('drafts'),
        folderNameToId('archive'),
    ];
    defaultFavoriteFolderIds.forEach(folderId =>
        tryAddSingleFavoriteFolderToStore(folderId, folderStore)
    );

    // 2. Load existing UserOptions.FavoriteNodes to store if there is any
    loadFavoriteNodesToStore();

    // 3. Migrate de-duped UserOptions.FavoriteFolders(used by JsMVVM) to FavoriteNodes(used by React), if there is any
    const userOptions = getUserConfiguration().UserOptions;
    if (userOptions.FavoriteFolders) {
        userOptions.FavoriteFolders.forEach(folderId => {
            tryAddSingleFavoriteFolderToStore(folderId, folderStore);
        });
    }

    // 4. Submit the service call to update UserOptions on the client and server
    updateFavoritesAndMarkMigrationComplete(userOptions);
});
